import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { Loader2, Upload, X, Users } from "lucide-react";
import { AIDescriptionAssistant } from "../components/AIDescriptionAssistant";

export default function TontineCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    description: "",
    image_url: "",
    currency: "XOF",
    contribution_amount: 10000,
    max_participants: 10,
    frequency: "monthly",
    frequency_days: 30,
    start_date: "",
    end_date: "",
    penalty_percent: 0,
    reminder_days_before: [3, 1, 0],
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd);
      set("image_url", data.path);
      setImagePreview(URL.createObjectURL(file));
    } catch {
      toast.error("Échec du téléchargement");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date) { toast.error("Date de démarrage requise"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        contribution_amount: parseFloat(form.contribution_amount),
        max_participants: parseInt(form.max_participants),
        frequency_days: form.frequency === "custom" ? parseInt(form.frequency_days) : null,
        penalty_percent: parseFloat(form.penalty_percent),
        end_date: form.end_date || null,
        image_url: form.image_url || null,
        description: form.description || null,
      };
      const { data } = await api.post("/tontines", payload);
      toast.success(`Tontine créée — ${data.id}`);
      navigate(`/tontines/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const totalTarget = form.contribution_amount * form.max_participants;
  const commission = Math.round(form.contribution_amount * 0.02);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="tontine-create-page">
      <div className="mb-8">
        <p className="text-sm text-muted-foreground mb-1">Sen Natt</p>
        <h1 className="font-display text-4xl font-bold tracking-tight">Créer une tontine</h1>
      </div>

      <form onSubmit={submit} className="card-soft p-6 sm:p-10 space-y-6">
        {/* Image */}
        <div>
          <Label>Image (optionnel)</Label>
          <div className="mt-2">
            {imagePreview ? (
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-secondary">
                <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => { setImagePreview(null); set("image_url", ""); }} className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="aspect-[16/9] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-primary text-muted-foreground hover:text-foreground transition">
                {uploading ? <Loader2 className="h-8 w-8 animate-spin" /> : <><Upload className="h-8 w-8 mb-2" /><span className="text-sm">Téléverser</span></>}
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" disabled={uploading} />
              </label>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="name">Nom de la tontine *</Label>
          <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="Ex: Tontine famille Diop 2026" data-testid="tontine-name" />
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label htmlFor="description">Description</Label>
            <AIDescriptionAssistant kind="tontine" value={form.description} onChange={(t) => set("description", t)} context={{ title: form.name, currency: form.currency, contribution_amount: form.contribution_amount, frequency: form.frequency, frequency_days: form.frequency_days, max_participants: form.max_participants, start_date: form.start_date, end_date: form.end_date, penalty_percent: form.penalty_percent }} />
          </div>
          <Textarea id="description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-xl" data-testid="tontine-description" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="amount">Montant cotisation (FCFA) *</Label>
            <Input id="amount" type="number" min="100" required value={form.contribution_amount} onChange={(e) => set("contribution_amount", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="tontine-amount" />
            <p className="text-xs text-muted-foreground mt-1">Commission Sen Natt : 2% = {commission.toLocaleString("fr-FR")} FCFA</p>
          </div>
          <div>
            <Label htmlFor="max">Nombre de participants *</Label>
            <Input id="max" type="number" min="2" max="200" required value={form.max_participants} onChange={(e) => set("max_participants", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="tontine-max" />
            <p className="text-xs text-muted-foreground mt-1">Cagnotte par tour : {totalTarget.toLocaleString("fr-FR")} FCFA</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Fréquence *</Label>
            <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="tontine-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Quotidienne</SelectItem>
                <SelectItem value="weekly">Hebdomadaire</SelectItem>
                <SelectItem value="monthly">Mensuelle</SelectItem>
                <SelectItem value="custom">Personnalisée</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.frequency === "custom" && (
            <div>
              <Label htmlFor="fdays">Nombre de jours entre cotisations</Label>
              <Input id="fdays" type="number" min="1" value={form.frequency_days} onChange={(e) => set("frequency_days", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="tontine-fdays" />
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="start">Date de démarrage *</Label>
            <Input id="start" type="date" required value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="tontine-start" />
          </div>
          <div>
            <Label htmlFor="end">Date de fin (optionnel)</Label>
            <Input id="end" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="tontine-end" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Pénalité de retard</Label>
            <span className="text-sm font-semibold gradient-text">{form.penalty_percent}%</span>
          </div>
          <Slider
            value={[form.penalty_percent]}
            onValueChange={(v) => set("penalty_percent", v[0])}
            min={0}
            max={10}
            step={1}
            data-testid="tontine-penalty"
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Appliquée automatiquement sur les cotisations en retard. {form.penalty_percent > 0 && `Soit ${Math.round(form.contribution_amount * form.penalty_percent / 100).toLocaleString("fr-FR")} FCFA par retard.`}
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-full">Annuler</Button>
          <Button type="submit" disabled={loading} className="btn-gradient rounded-full flex-1 h-11" data-testid="submit-tontine">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Users className="h-4 w-4 mr-2" />Créer la tontine</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
